
import React, { useState } from 'react';

interface DemoStep {
  title: string;
  description: string;
  icon: string;
  color: string;
  mockup: React.ReactNode;
}

const demoSteps: DemoStep[] = [
  {
    title: "Calendar Inteligent",
    description: "Sincronizare automată cu Portalul Instanțelor. Primești alerte pentru fiecare termen nou, fără să verifici manual.",
    icon: "📅",
    color: "from-blue-600 to-indigo-600",
    mockup: (
      <div className="bg-white border border-slate-100 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl w-full max-w-[280px] sm:max-w-[320px] mx-auto animate-float">
        <div className="flex justify-between items-center mb-6 sm:mb-8">
          <div className="flex flex-col">
            <span className="text-[9px] sm:text-[10px] font-black text-indigo-600 uppercase tracking-widest">Iunie 2024</span>
            <span className="text-xl sm:text-2xl font-black italic text-slate-900">Miercuri, 12</span>
          </div>
          <div className="px-2 sm:px-3 py-1 bg-emerald-100 text-emerald-600 text-[7px] sm:text-[8px] font-black rounded-full border border-emerald-200 shadow-sm">Portal Sync</div>
        </div>
        <div className="space-y-3 sm:space-y-4">
          <div className="p-4 sm:p-5 bg-indigo-50 rounded-2xl border-l-4 border-indigo-600 shadow-sm">
            <p className="text-[7px] sm:text-[8px] font-black text-slate-400 uppercase mb-1 tracking-tight">Dosar 123/299/2024 • Jud. S1</p>
            <p className="text-[10px] sm:text-xs font-black italic text-slate-800">🏛️ Ședință: Fond - Probe</p>
            <div className="flex items-center gap-2 mt-2 sm:mt-3">
              <span className="text-[9px] sm:text-[10px] text-indigo-600 font-bold">Ora: 09:30</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase">Sala 2</span>
            </div>
          </div>
          <div className="p-4 sm:p-5 bg-slate-50 rounded-2xl opacity-40 border border-slate-100">
            <p className="text-[7px] sm:text-[8px] font-black text-slate-400 uppercase mb-1">Dosar 456/200/2024</p>
            <p className="text-[10px] sm:text-xs font-black italic text-slate-400">📅 Pronunțare Soluție</p>
          </div>
        </div>
      </div>
    )
  },
  {
    title: "Analiză Documente AI",
    description: "Încarcă un PDF sau o poză a unei decizii. AI-ul extrage automat termenele procedurale și face un rezumat executiv.",
    icon: "📂",
    color: "from-emerald-600 to-teal-600",
    mockup: (
      <div className="bg-slate-900 rounded-[2.5rem] sm:rounded-[3rem] p-6 sm:p-8 shadow-2xl w-full max-w-[280px] sm:max-w-[320px] mx-auto border border-white/10 animate-float">
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/20 rounded-2xl flex items-center justify-center text-xl sm:text-2xl shadow-inner border border-emerald-500/20 text-emerald-400">📄</div>
          <div>
            <p className="text-[8px] sm:text-[10px] font-black text-emerald-400 uppercase tracking-widest">Decizie_Fond.pdf</p>
            <div className="h-1 w-20 sm:w-24 bg-emerald-500/20 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-emerald-500 w-full animate-progress"></div>
            </div>
          </div>
        </div>
        <div className="space-y-4 sm:space-y-5">
          <div className="bg-white/5 p-4 sm:p-5 rounded-2xl border border-white/5">
            <p className="text-[9px] sm:text-[10px] font-black text-indigo-400 uppercase mb-2 sm:mb-3 flex items-center gap-2">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-indigo-500 rounded-full animate-pulse"></span>
              Rezumat AI
            </p>
            <p className="text-[10px] sm:text-[11px] text-slate-300 leading-relaxed italic font-medium">
              "Instanța admite excepția prescripției. Apelul poate fi declarat în 30 de zile de la comunicare."
            </p>
          </div>
          <div className="bg-emerald-500/10 p-3 sm:p-4 rounded-xl border border-emerald-500/20 text-center">
             <p className="text-[8px] sm:text-[9px] font-black text-emerald-400 uppercase tracking-[0.2em]">🚨 Limită: 12.07.2024</p>
          </div>
        </div>
      </div>
    )
  },
  {
    title: "Chat & Draft AI",
    description: "Întreabă AI-ul despre orice articol de lege sau cere-i să redacteze un contract. Totul se bazează pe contextul tău.",
    icon: "🤖",
    color: "from-indigo-600 to-purple-600",
    mockup: (
      <div className="bg-white border border-slate-100 rounded-[2.5rem] sm:rounded-[3rem] p-5 sm:p-6 shadow-2xl w-full max-w-[280px] sm:max-w-[320px] mx-auto flex flex-col h-[280px] sm:h-[320px] animate-float">
        <div className="flex-grow space-y-4 sm:space-y-5 overflow-hidden">
          <div className="flex justify-end">
            <div className="bg-indigo-600 text-white p-3 sm:p-4 rounded-2xl rounded-tr-none text-[9px] sm:text-[10px] font-bold max-w-[85%] shadow-md">Redactează un draft de notificare pentru plată.</div>
          </div>
          <div className="flex justify-start">
            <div className="bg-slate-50 border border-slate-100 p-3 sm:p-4 rounded-2xl rounded-tl-none text-[9px] sm:text-[10px] text-slate-700 leading-relaxed italic max-w-[90%] shadow-sm">
              <span className="font-black text-indigo-600 uppercase block mb-1 text-[7px] sm:text-[8px]">LexSmart AI</span>
              Conform specificațiilor tale, am generat draftul. Am inclus temeiul legal din Art. 1522 Cod Civil...
            </div>
          </div>
        </div>
        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-slate-50 rounded-2xl border border-slate-100 flex justify-between items-center">
          <div className="flex gap-1">
            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-100"></div>
          </div>
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg text-lg">🚀</div>
        </div>
      </div>
    )
  }
];

interface DemoModalProps {
  onClose: () => void;
  onRegister: () => void;
}

const DemoModal: React.FC<DemoModalProps> = ({ onClose, onRegister }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const next = () => {
    if (currentStep < demoSteps.length - 1) setCurrentStep(currentStep + 1);
    else onRegister();
  };

  const prev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const step = demoSteps[currentStep];

  return (
    <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-2xl z-[200] flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-6xl bg-white rounded-[2.5rem] md:rounded-[4rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row h-full max-h-[92vh] lg:h-[700px] animate-in zoom-in-95 duration-300">
        
        {/* Vizual Area - 35% on mobile, 50% on desktop */}
        <div className={`w-full lg:w-1/2 bg-gradient-to-br ${step.color} p-6 sm:p-10 lg:p-20 flex items-center justify-center relative overflow-hidden transition-all duration-700 shrink-0 h-[35%] lg:h-full`}>
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_white_0%,_transparent_70%)] opacity-20"></div>
          </div>
          <div className="relative z-10 w-full flex items-center justify-center scale-90 sm:scale-100 lg:scale-110">
            {step.mockup}
          </div>
        </div>

        {/* Info Area - 65% on mobile, 50% on desktop with internal scroll */}
        <div className="w-full lg:w-1/2 p-6 sm:p-10 lg:p-20 flex flex-col bg-white overflow-hidden h-[65%] lg:h-full">
          
          {/* Top Bar with Pagination */}
          <div className="flex justify-between items-center mb-6 lg:mb-12 shrink-0">
            <div className="flex gap-2 sm:gap-3">
              {demoSteps.map((_, i) => (
                <div key={i} className={`h-1 rounded-full transition-all duration-500 ${i === currentStep ? 'w-8 lg:w-12 bg-indigo-600' : 'w-2 lg:w-4 bg-slate-100'}`}></div>
              ))}
            </div>
            <button onClick={onClose} className="text-slate-300 hover:text-rose-500 text-3xl lg:text-5xl font-light transition-colors leading-none p-2">×</button>
          </div>

          {/* Scrollable Content Container */}
          <div className="flex-grow overflow-y-auto custom-scrollbar pr-2 mb-6">
            <div className="py-4">
              <span className="text-4xl lg:text-7xl mb-4 lg:mb-10 inline-block animate-bounce">{step.icon}</span>
              <h3 className="text-2xl sm:text-3xl lg:text-7xl font-black text-slate-900 mb-4 lg:mb-10 italic tracking-tighter leading-[1.1]">
                {step.title}
              </h3>
              <p className="text-slate-500 text-sm sm:text-base lg:text-3xl leading-relaxed italic font-medium">
                {step.description}
              </p>
            </div>
          </div>

          {/* Action Buttons - Always at bottom */}
          <div className="mt-auto flex flex-col sm:flex-row gap-3 sm:gap-4 shrink-0 pb-2">
            {currentStep > 0 && (
              <button 
                onClick={prev}
                className="px-6 lg:px-12 py-4 lg:py-8 border border-slate-100 rounded-xl lg:rounded-[2rem] font-black text-[9px] lg:text-[11px] uppercase tracking-widest text-slate-400 hover:bg-slate-50 transition-all"
              >
                Înapoi
              </button>
            )}
            <button 
              onClick={next}
              className="flex-grow py-4 lg:py-8 bg-indigo-600 text-white rounded-xl lg:rounded-[2rem] font-black text-[9px] lg:text-[11px] uppercase tracking-widest shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all active:scale-95"
            >
              {currentStep === demoSteps.length - 1 ? 'ÎNCEPE ACUM - GRATUIT' : 'URMĂTORUL PAS →'}
            </button>
          </div>
          
          <p className="text-center mt-6 lg:mt-12 text-[8px] lg:text-[10px] font-black text-slate-200 uppercase tracking-[0.4em] shrink-0">
            LexSmart AI • Cabinetul Juridic Digital
          </p>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        .animate-progress {
          animation: progress 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default DemoModal;
